import { useQuery } from '@tanstack/react-query';
import { useClient } from '@utils/api-client';
import { BillingAccountPolicySearchRequestQuery, BillingAccountPolicySearchResponse } from '@lio/api-types';
import { buildQueryParamForArray } from '@utils/buildQueryParamForArray';
import { BillingAccountPolicyStatus } from '@lio/rule-engine';

const config = {
  staleTime: 60000,
};

type BillingAccountPolicySearchQueryParams = BillingAccountPolicySearchRequestQuery & {
  hasBalance?: boolean;
};

export const BillingAccountPolicyKeys = {
  search: ['billing-account-policies'] as const,
  searchParams: (params: BillingAccountPolicySearchQueryParams) =>
    [...BillingAccountPolicyKeys.search, params] as const,
};

const getQueryParams = (params: BillingAccountPolicySearchQueryParams) => {
  const { sort, pageLimit, pageOffset, billingAccountId } = params;
  const query = new URLSearchParams();

  if (sort) {
    query.append('sort', sort);
  }

  if (pageLimit) {
    query.append('pageLimit', String(pageLimit));
  }

  if (pageOffset) {
    query.append('pageOffset', String(pageOffset));
  }

  if (billingAccountId) {
    query.append('billingAccountId', String(billingAccountId));
  }

  if (params.hasBalance !== undefined) {
    query.append('hasBalance', String(params.hasBalance));
  }

  buildQueryParamForArray(query, 'status', [
    BillingAccountPolicyStatus.Active,
    BillingAccountPolicyStatus.Canceled,
    BillingAccountPolicyStatus.PendingCancelation,
    BillingAccountPolicyStatus.Expired,
  ]);

  return query.toString();
};

export const useSearchBillingAccountPolicies = (params: BillingAccountPolicySearchQueryParams, enabled?: boolean) => {
  const client = useClient();
  const query = getQueryParams({ ...params });
  const requestUrl = `billing-account-policy?${query}`;

  const forceDisabled = !params.billingAccountId;
  const result = useQuery<any, Error>({
    queryKey: BillingAccountPolicyKeys.searchParams({ ...params }),
    queryFn: async () => {
      const response = await client.get(requestUrl).json<BillingAccountPolicySearchResponse>();
      return response;
    },
    enabled: enabled && !forceDisabled,
    ...config,
  });

  return { ...result, items: result.data?.items as BillingAccountPolicySearchResponse['items'] };
};

// TEMPORARY MOCK DATA - TPaymentHistoryRow will come from api types eventually
